import React, { useState, useEffect } from 'react';
import Select from "react-select";
import { TrashIcon } from "@heroicons/react/solid";
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import WithScrollbar from '../WithScrollbar';
import TitleEditable from "../../components/TitleEditable";
import { updateItemsOfCollection, updateCollection } from '../../actions/settings';
import imgDndIndicator from '../../assets/images/dnd_indicator.svg';
import "react-multi-carousel/lib/styles.css";
import './ProcessingArea.scss';

const ProcessingArea = props => {
  const dispatch = useDispatch();
  const userSettings = useSelector(({ settings }) => settings);

  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadingItems, setUploadingItems] = useState([]);
  const [currentItems, setCurrentItems] = useState([]);
  const [shareWithValue, setShareWithValue] = React.useState(null);
  const [isMultiSelect, setIsMultiSelect] = React.useState(true);

  // items
  useEffect(() => {
    const currentItemIds = currentItems.map(it => it.id);
    const newRestItems = (props.items || []).filter(it => !currentItemIds.includes(it.id));
    const newCurrentItems = (props.items || []).filter(it => currentItemIds.includes(it.id));
    let newItems = [];

    currentItemIds.forEach(citId => {
      const findItem = newCurrentItems.find(it => it.id === citId);

      if (findItem) {
        newItems.push(findItem);
      }
    });

    newItems = [...newItems, ...newRestItems];
    const itemIds = newItems.map(it => it.id);
    const newUploadingItems = uploadingItems.filter(uit => !itemIds.includes(uit.id));
    setCurrentItems(newItems);
    setUploadingItems(newUploadingItems);
  }, [props.items]);
  
  // collection
  useEffect(() => {
    const itemIds = (props.items || []).map(it => it.id);
    const newUploadingItems = uploadingItems.filter(uit => !itemIds.includes(uit.id));
    setCurrentItems(props.items || []);
    setUploadingItems(newUploadingItems);

    const sharedWith = _.get(props.collection, 'sharedWith', null);

    if (!sharedWith) {
      setShareWithValue(null);
      setIsMultiSelect(false);
    } else if (sharedWith.length === 1 && sharedWith[0] === 'ALL') {
      setIsMultiSelect(false);
      setShareWithValue({ value: 'ALL', label: 'All team members' });
    } else {
      const sharedUsers = _.get(userSettings, 'users', []).filter(usr => sharedWith.includes(usr.userId)).map(usr => ({ value: usr.userId, label: usr.name }));
      setShareWithValue(sharedUsers);
    }
  }, [props.collection]);

  // //////////////////////////////////////////////////////////////////////////////////////////////////
  // Drag and drop

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
    return false;
  }

  const handleDrop = e => {
    const itemData = JSON.parse(e.dataTransfer.getData('item'));
    setIsDragOver(false);

    if (![...props.items, ...uploadingItems].find(it => it.id === itemData.id)) {
      props.addItemToCollection(itemData.id, props.collection.id);
      const newUploadingItems = [...uploadingItems, { ...itemData }];
      setUploadingItems(newUploadingItems);
    }

    e.dataTransfer.clearData();
  }

  const onMouseLeave = (e) => {
    setIsDragOver(false);
  }

  // //////////////////////////////////////////////////////////////////////////////////////////////////
  // Collection

  const onDeleteCollection = (e) => {
    e.stopPropagation();
    props.onDeleteCollection(props.collection);
  }

  const onClickPublish = (e) => {
    e.stopPropagation();

    if (props.site) {
      onDeleteSite(props.site.id);
    } else {
      props.onPublishMicrosite();

      if (props.collection.sharedBy) {
        const { id, sharedBy, sharedWith } = props.collection;
        dispatch(updateItemsOfCollection(currentItems.map(cit => cit.id), id, sharedBy, sharedWith));
      } else {
        dispatch(updateItemsOfCollection(currentItems.map(cit => cit.id), props.collection.id));
      }
    }
  }

  const onRemoveItem = (itemId) => {
    props.onRemoveItem(itemId, props.collection.id);
  }

  const onDeleteSite = (id) => {
    props.onDeleteSite(id);
  }

  const itemsChangeHandler = items => {
    setCurrentItems(items);
  }

  const onSaveHandler = e => {
    props.onEndEditing();

    if (shareWithValue) {
      const shareWith = Array.isArray(shareWithValue) ? shareWithValue.map(sv => sv.value) : ['ALL'];

      dispatch(updateItemsOfCollection(
        currentItems.map(cit => cit.id),
        props.collection.id,
        _.get(userSettings, 'userInfo.userId', ''),
        shareWith
      ));
    } else {
      dispatch(updateItemsOfCollection(
        currentItems.map(cit => cit.id),
        props.collection.id
      ));
    }
  }

  const handleShareWithChange = value => {
    if (value) {
        if (Array.isArray(value)) {
          const singleValue = value.find(val => val.value === 'ALL' || val.value === null);

          if (singleValue) {
            setIsMultiSelect(false);

            if (singleValue.value) {
              setShareWithValue(value[value.length - 1]);
            } else {
              setShareWithValue(null);
            }
          } else {
            setIsMultiSelect(true);
            setShareWithValue(value);
          }
        } else {
          if (value.value === 'ALL' || value.value === null) {
            setIsMultiSelect(false);

            if (value.value === null) {
              setShareWithValue(null);
            } else {
              setShareWithValue(value);
            }
          } else {
            setIsMultiSelect(true);
            setShareWithValue([value]);
          }
        }
    } else {
      setShareWithValue(null);
    }
  };

  const generateOptions = () => {
    return [
      { value: 'ALL', label: 'All team members' },
      { value: null, label: 'No team members' },
      ..._.get(userSettings, 'users', [])
        .filter(usr => usr.userId !== _.get(userSettings, 'userInfo.userId', '') && !usr.disabled)
        .map(usr => ({ value: usr.userId, label: usr.name }))
    ];
  };

  const onChangeCollectionTitle = value => {
    dispatch(updateCollection(
      props.collection.id,
      {
        title: value
      }
    ))
  }

  const sharedBy = _.get(props.collection, 'sharedBy', null);
  const userId = _.get(userSettings, 'userInfo.userId', '');
  const userType = _.get(userSettings, 'userInfo.type', '');
  const isSharedCollection = sharedBy === null || sharedBy === userId || userType === 'admin';
    
  return (
    <div className="processing-area">
      <div className="header-box d-flex">
        <div className="header-container">
          <div className="flex items-start justify-between">
            <div className="title-box">
              <div className="label">Collection:</div>
              <TitleEditable
                onChange={(value) => onChangeCollectionTitle(value)}
                value={props.collection.title}
                className="mb-2"
                editable={isSharedCollection}
              />
            </div>
            <div className="flex items-center" style={{ marginTop: 2 }}>
              {isSharedCollection &&
                <div
                  variant="outline-danger"
                  size="sm"
                  className="ml-2 btn btn-outline-danger btn-sm"
                  onClick={onSaveHandler}
                >
                  Save
                </div>
              }
              <div
                variant="outline-danger"
                size="sm"
                className={`${ isSharedCollection ? 'ml-2' : 'ml-5'} btn btn-outline-danger btn-sm`}
                onClick={onClickPublish}
              >
                {props.site ? 'Unpublish' : 'Publish'}
              </div>
              {isSharedCollection && <TrashIcon className="ml-2 delete-collection" onClick={onDeleteCollection} />}
            </div>
          </div>
          {isSharedCollection &&
            <div className="flex justify-between mt-2" onClick={e => e.stopPropagation()}>
              <div className="selector-name">Share with...</div>
              {isMultiSelect ?
                <Select
                  className="w-100 select-box funnel-stage"
                  classNamePrefix="share_"
                  closeOnSelect={true}
                  isMulti
                  onChange={value => handleShareWithChange(value)}
                  options={generateOptions()}
                  placeholder="No team members"
                  removeSelected={true}
                  value={shareWithValue}
                />
                :
                <Select
                  className="w-100 select-box funnel-stage"
                  classNamePrefix="share_"
                  onChange={value => handleShareWithChange(value)}
                  options={generateOptions()}
                  placeholder="No team members"
                  removeSelected={true}
                  value={shareWithValue}
                />
              }
            </div>
          }
        </div>
      </div>
      <div>
        {isSharedCollection ?
          <div
            className={`d-flex processing-content-area ${isDragOver ? 'dragging' : ''}`}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onMouseLeave={onMouseLeave}
          >
            {
              (currentItems.length > 0 || uploadingItems.length > 0) && (
                <WithScrollbar
                  items={currentItems}
                  uploadingItems={uploadingItems}
                  onRemoveItem={onRemoveItem}
                  itemsChange={itemsChangeHandler}
                />
              )
            }

            {
              (currentItems.length === 0 && uploadingItems.length === 0) && (
                <div className="dnd-empty-state-box">
                  <img src={imgDndIndicator} alt="drag and drop indicator" />
                </div>
              )
            }
          </div>
          :
          <div />
        }
      </div>
    </div>
  )
}

export default ProcessingArea;
